import React, { useState, useEffect, useRef } from "react";
import SimpleKeyboard from "react-simple-keyboard";
import _toNumber from "lodash/toNumber";
import _get from "lodash/get";
import { ModalHeader, openTouchInput, closeTouchInput } from "./index";
import translateText from "../translate";
import { uniqueId } from "lodash";

const BUTTON_THEME = [
  {
    class: "keyboard-space",
    buttons: "{space}",
  },
  {
    class: "keyboard-enter",
    buttons: "{enter}",
  },
];

function buildShiftHtml(isCapsLock) {
  return `
    <span style="display: flex; align-items: center;">
      <span style="${isCapsLock ? "border-bottom: 5px solid;" : "margin-top: -5px;"} margin-left: -10px; margin-right: 10px;">
        &uarr;
      </span> ${translateText("shift")}
    </span>
  `;
}

export function setCaretPoisition(start, end, direction) {
  Keyboard.setCaretPoisition(start ?? 0, end ?? 0, direction ?? "forward");
}

export default function Keyboard(props) {
  const keyboardRef = useRef();

  let { inputName, defaultValue, element, type, keyboardLayout, units, onBack, system_info, isOpen, showValue, config, isElementHidden } = props;

  let value = (defaultValue || {}).value;
  if (typeof value === "number") value = value.toString();
  let defaultLayoutName = system_info.language || "en" || "multiline";
  const keyboardWidth = type === "numeric" ? 100 : system_info.keyboard_width_percent || 100;

  const [layoutName, setLayoutName] = useState(defaultLayoutName);
  const [unit, setUnit] = useState("");
  const [shiftPressCount, setShiftPressCount, tabPressCount, setTabPressCount, setCaretPosition] = useState(0);
  const [shiftDisplay, setShiftDisplay] = useState(buildShiftHtml());
  const [leftValue, setLeftValue] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const keyboardDisplay = {
    "{enter}": translateText("enter"),
    "{bksp}": translateText("backspace"),
    "{space}": translateText("space"),
    "{123}": "123",
    "{abc}": "abc",
    "{empty}": " ",
    "{larr}": "&larr;",
    "{rarr}": "&rarr;",
    "{shift}": shiftDisplay,
    "{tab}": "TAB",
    "{bulletpoint}": "\u25E6 ---", // Bullet point button
  };

  Keyboard.setCaretPoisition = function (start, end, direction) {
    if (!element) return;
    if (start !== end) return;

    element.selectionStart = start;
    element.selectionEnd = end;
    element.selectionDirection = direction;

    keyboardRef.current.setCaretPosition(start);
  };

  useEffect(() => {
    setShiftPressCount(0);
    setShiftDisplay(buildShiftHtml());
  }, [isOpen]);

  // whenever opening a new touchinput Keyboard...
  useEffect(() => {
    if (showValue) setLeftValue("");
    if (!element) return;
    setShiftPressCount(0);
    setShiftDisplay(buildShiftHtml());
    // set the keyboard's internal value and inputName
    keyboardRef.current.setInput(showValue ? "" : value, inputName);

    // set the layout type
    if (type === "numeric") {
      //NUMERIC TYPE VALUE
      setLayoutName("numeric");
    } else if (type === "multiline") {
      //SET TO MULTILINE KEYBOARD
      setLayoutName("multiline");
    } else setLayoutName(defaultLayoutName);
    
    // sync the caret position
    keyboardRef.current.setCaretPosition(element.selectionStart || (value || "").length);

    // pass initial state for "unit"
    setUnit(props.unit);
  }, [element]);

  // sync caret position on layout change
  useEffect(() => {
    if (element && !isNaN(element.selectionEnd)) {
      keyboardRef.current.setCaretPosition(element.selectionEnd);
    }
  }, [layoutName]);

  Keyboard.clearInput = function () {
    keyboardRef.current.clearInput();
  };

  // caretDelta is used to keep track of caret displacement
  let caretDelta;

  const onKeyPress = (button) => {
    const input = element;
    if (input && !isElementHidden) input.focus();
    let currentInput = keyboardRef.current.getInput() || "";
    let caretPosition = input.selectionStart;
    caretDelta = button.length;
    if (button === "{enter}") {
      if (type === "multiline") {
        // UPDATE LATER WHEN DATABASE IS UPDATED AND THE KEYBOARD REF IT IS CORRECT
        // Get the current input and caret position from the virtual keyboard
        let currentInput = keyboardRef.current.getInput() || ""; // Ensure input is not undefined
        const caretPosition = keyboardRef.current.getCaretPosition() || currentInput.length || 0;

        // Insert a newline at the current caret position
      const updatedInput = currentInput.slice(0, caretPosition) + "\n" + currentInput.slice(caretPosition);

        // Update the keyboard input
        keyboardRef.current.setInput(updatedInput);

        // Move the caret to the new position after the newline
        keyboardRef.current.setCaretPosition(caretPosition + 1);

        // Ensure the onChange function is triggered to update the UI immediately
        if (props.onChange) props.onChange(updatedInput);
        // Scroll the textarea to the bottom
        if (input) {
          input.scrollTop = input.scrollHeight + 20; // Scroll to the bottom
        }

        return;
      }
      if (props.onPressEnter) {
        const str = keyboardRef.current.getInput() || value;
        let msg = "";
        if (type === "numeric") {
          const validations = _get(config, "validations", []);
          for (const v in validations) {
            const { min, max, err_msg } = validations[v];
            const val = _toNumber(str);
            if (val > max || val < min) {
              msg = err_msg;
              break;
            }
          }
        }
        setErrorMsg(msg);
        if (!msg) props.onPressEnter(str);
        if (props.clearOnEnter) {
          input.text = "";
          input.selectionStart = input.selectionEnd = 0;
          keyboardRef.current.clearInput();
        }
      } else {
        closeTouchInput();
        return;
      }
    } else if (button === "{shift}") {
      if (shiftPressCount === 0) {
        setShiftPressCount(1);
        setLayoutName(`${layoutName}_shift`);
      } else if (shiftPressCount === 1) {
        setShiftPressCount(2);
        setShiftDisplay(buildShiftHtml(true));
      } else if (shiftPressCount === 2) {
        setShiftPressCount(0);
        setShiftDisplay(buildShiftHtml());
        setLayoutName(layoutName.split("_")[0]);
      } else if (button === "{tab}") {
        // Handle Tab key
        if (tabPressCount === 0) {
          // First tab press: Insert a tab character into the input field
          let currentInput = keyboardRef.current.getInput();
          currentInput += "\t"; // Insert a tab character
          keyboardRef.current.setInput(currentInput);
          setTabPressCount(1); // Move to the next step (optionally, this can be skipped)
          setCaretPosition(currentInput.length); // Move caret to the end
        }
      }
      return;
      //Bullet point
    } else if (button === "{bulletpoint}") {
      // Get the current input and caret position
      let currentInput = keyboardRef.current.getInput() || ""; // Ensure input is not undefined
      const caretPosition = keyboardRef.current.getCaretPosition();
      // Insert a bullet point at the current caret position
      const updatedInput = currentInput.slice(0, caretPosition) + "• " + currentInput.slice(caretPosition);
      // Update the keyboard input
      keyboardRef.current.setInput(updatedInput);
      // Move the caret to the position after the inserted bullet point
      keyboardRef.current.setCaretPosition(caretPosition + 2); // Account for "• "
      // Trigger an onChange to ensure immediate UI synchronization to add the bullet point when clicked
      if (props.onChange) props.onChange(updatedInput);
      return;
      // } else if (button === "{newline}") {
      //   // Get the current input and caret position from the virtual keyboard
      //   let currentInput = keyboardRef.current.getInput() || ""; // Ensure input is not undefined
      //   const caretPosition = keyboardRef.current.getCaretPosition();

      //   // Insert a newline at the current caret position
      //   const updatedInput = currentInput.slice(0, caretPosition) + "\n" + currentInput.slice(caretPosition);

      //   // Update the keyboard input
      //   keyboardRef.current.setInput(updatedInput);

      //   // Move the caret to the new position after the newline
      //   keyboardRef.current.setCaretPosition(caretPosition + 1);

      //   // Ensure the onChange function is triggered to update the UI immediately
      //   if (props.onChange) props.onChange(updatedInput);

      //   return;
    } else if (button === "{123}") {
      setLayoutName("text_numeric");
      return;
    } else if (button === "{abc}") {
      setLayoutName(defaultLayoutName);
      setShiftPressCount(0);
      setShiftDisplay(buildShiftHtml());
      return;
    } else if (button === "{larr}") {
      // do nothing if already at leftmost caret position
      if (input.selectionStart === 0) return;

      const nextCaretPos = input.selectionStart - 1;
      input.selectionStart = input.selectionEnd = nextCaretPos;
      keyboardRef.current.setCaretPosition(nextCaretPos);

      if (!isElementHidden) {
        // ensure caret is in view
        input.blur();
        input.focus();
      }

      if (props.onUpdateCaret) props.onUpdateCaret(nextCaretPos);

      return;
    } else if (button === "{rarr}") {
      // do nothing if already at rightmost caret position
      const str = keyboardRef.current.getInput();
      if (input.selectionStart === str.length) return;

      const nextCaretPos = input.selectionStart + 1;
      input.selectionStart = input.selectionEnd = nextCaretPos;
      keyboardRef.current.setCaretPosition(nextCaretPos);

      if (!isElementHidden) {
        // ensure caret is in view
        input.blur();
        input.focus();
      }

      if (props.onUpdateCaret) props.onUpdateCaret(nextCaretPos);

      return;
      //backspace button,
    } else if (button === "{bksp}") {
      if (inputName === "medication_name") {
        let caretPosition = keyboardRef.current.getInput().length;
        // do nothing if already at leftmode caret position
        if (input.selectionStart === 0) return;
        // Correctly remove the character just before the caret
        currentInput = currentInput.slice(0, caretPosition - 1) + currentInput.slice(caretPosition);
        caretDelta = -1;
        //go ahead one space if it is clicked
      } else {
        //CHECK IF THERE EXIST'S A CURSOR-CARET
        if (isNaN(keyboardRef.current.getCaretPosition())) {
          currentInput = currentInput.slice(0, currentInput.length - 1);
          caretDelta = -1;
        } else {
          if (caretPosition > currentInput.length) {
            //Check if the caret it is bigger than the INPUT to prevent caret going to the units
            keyboardRef.current.setCaretPosition(currentInput.length);
            caretPosition = currentInput.length;
          }
          // Do nothing if already at the leftmost caret position
          if (caretPosition === 0) return;

          // Correctly remove the character just before the caret
          currentInput = currentInput.slice(0, caretPosition - 1) + currentInput.charAt(caretPosition - 2) + currentInput.slice(caretPosition);

          // Update caret position
          caretDelta = -1;
          //go ahead one space if it is clicked
        }
      }
    } else if (button === "{space}") {
      caretDelta = 1;
    } else if (
      button !== "{enter}" &&
      button !== "{shift}" &&
      button !== "{tab}" &&
      button !== "{bulletpoint}" &&
      button !== "{larr}" &&
      button !== "{rarr}"
    ) {
      // Add character for non-special buttons (like a-z, 0-9)
      // currentInput = currentInput.slice(0, caretPosition) + button + currentInput.slice(caretPosition);
      // Initialize caretDelta
      caretDelta = 1; // Move caret forward by 1 after adding a character

      // Adjust caretDelta if caret is not at the end of the string to adjust where the new value it is added
      if (caretPosition < currentInput.length) {
        caretDelta -= 1; // Move caret one step back to its original position if it's not at the end
      }
    }

    // Update the input field with the modified string
    keyboardRef.current.setInput(currentInput);

    // Update the caret position after the modification
    keyboardRef.current.setCaretPosition(caretPosition + caretDelta);

    // Trigger onChange to propagate changes to the parent component
    if (props.onChange) {
      const nextCaretPos = caretPosition + caretDelta;
      props.onChange(currentInput, unit, nextCaretPos);
    }

    // Sync the caret position in the element
    input.selectionStart = input.selectionEnd = caretPosition + caretDelta + 1;
  };

  const onChange = (text) => {
    const input = element;

    // ensure value is a number
    if (type === "numeric" && isNaN(_toNumber(text)) && text !== "-") {
      keyboardRef.current.setInput(text.slice(0, -1));
      keyboardRef.current.setCaretPosition(input.selectionStart);
      return; // do nothing if not a number
    }

    // apply caretDelta to caret position
    let nextCaretPos = input.selectionStart + caretDelta;
    if (props.onChange) props.onChange(text, unit, nextCaretPos);
    if (showValue) setLeftValue(text);

    if (isElementHidden) {
      input.value = text;
    }
    // apply update to caret position after setting input value
    input.selectionStart = input.selectionEnd = nextCaretPos;

    if (!isElementHidden) {
      // ensure caret is in view
      input.blur();
      input.focus();
    }

    if (layoutName.includes("_shift") && shiftPressCount === 1) {
      setLayoutName(layoutName.split("_")[0]);
      setShiftDisplay(buildShiftHtml());
      setShiftPressCount(0);
    }
  };

  const contentStyle = { ...styles.keyboardContentStyle, width: `${keyboardWidth}%` };

  return (
    <>
      <ModalHeader
        id="keyboard"
        onDelete={() => {
          if (props.onChange) props.onChange("");
          keyboardRef.current.clearInput();
          element.focus();
        }}
        title={translateText(inputName)}
        onBack={onBack ? () => onBack(keyboardRef.current.getInput()) : null}
        uniqueId={props.uniqueId}
        useTouchInputNav={props.useTouchInputNav}
      />
      {errorMsg && <div style={{ marginBottom: -18, marginTop: 18 }}>{translateText(errorMsg)}</div>}
      <div style={contentStyle}>
        {showValue && (
          <div style={styles.leftValueContainer}>
            <div style={styles.leftValue}>
              <div>{translateText(inputName)}</div>
              <div>{leftValue || value || "N/A"}</div>
              <div>{(units || [])[0]}</div>
            </div>
          </div>
        )}
        <div style={{ maxWidth: type === "numeric" ? "750px" : null, width: "100%", display: "flex" }}>
          {/* Keyboard */}
          <div style={{ flex: 1 }}>
            <SimpleKeyboard
              inputName={inputName}
              preventMouseDownDefault
              preventMouseUpDefault
              disableButtonHold
              layoutName={layoutName}
              buttonTheme={BUTTON_THEME}
              layout={keyboardLayout}
              display={keyboardDisplay}
              keyboardRef={(r) => (keyboardRef.current = r)}
              onChange={onChange}
              onKeyPress={onKeyPress}
            />
          </div>
        </div>

        {renderUnitSelect()}
      </div>
    </>
  );

  function renderUnitSelect() {
    if (!units) return;
    return (
      <div style={styles.unitSelectWrapper}>
        <div style={{ overflowY: "scroll", height: 90 * 4 + 70 }}>
          {units.map((u) => {
            const style = { ...styles.unitButton };
            let isActive = unit === u;
            if (u === "custom" && !units.includes(unit)) isActive = true;
            if (isActive) style.backgroundColor = "var(--secondary)";
            return (
              <div
                style={style}
                key={u}
                onMouseDown={(e) => {
                  e.preventDefault();
                  // custom unit select
                  if (u === "custom") {
                    const str = keyboardRef.current.getInput();
                    keyboardRef.current.clearInput();
                    setLayoutName(defaultLayoutName);
                    element.selectionStart = element.selectionEnd = -1;
                    return openTouchInput({
                      element: props.element,
                      ignoreCaretPos: true,
                      inputName: props.inputName,
                      onChange: (e) => {
                        if (props.onChange) props.onChange(str, e);
                      },
                      defaultValue: "",
                      type: "text" || "multiline",
                    });
                  }

                  setUnit(u);
                  const str = keyboardRef.current.getInput();
                  if (props.onChange) props.onChange(str, u);

                  const caretPos = element.selectionStart;

                  // persist caret position
                  setTimeout(() => {
                    element.selectionStart = element.selectionEnd = caretPos;
                  }, 10);
                }}
              >
                {translateText(u, true)}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}
const styles = {
  keyboardContentStyle: {
    justifyContent: "center",
    paddingTop: "6px",
    display: "flex",
    margin: "auto",
    marginBottom: "-10px",
  },
  unitSelectWrapper: {
    marginTop: 5,
    marginBottom: 20,
    marginLeft: 80,
  },
  unitButton: {
    height: 70,
    marginBottom: 20,
    backgroundColor: "rgba(255,255,255,.3)",
    width: 200,
    borderRadius: 5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: ".1s",
  },
  leftValue: {
    color: "white",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: 60,
    flexDirection: "column",
    lineHeight: 1.4,
    backgroundColor: "var(--grey)",
    minWidth: 320,
    minHeight: 320,
    paddingRight: 40,
    paddingLeft: 40,
    borderRadius: 20,
  },
  leftValueContainer: {
    width: 750,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};
